// DON'T ADD IMPORTS FROM CONTENTDEFINITION HERE. IT CAUSES BUILD ERRORS FOR SOME REASON.
import { createSelector } from 'reselect';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ExploreContentApi_ContentType } from '~src/spacesPlatform/models/IContent';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { selectFlowsOnSiteEnabled } from '../../flow/selectors/FlowSelectors';
import { ContentFilterKeyName } from '../models/ContentFilters';
import { ExplorableItemType } from '../models/ContentType';
import { IFilterClause, IStringValuedFilterClause } from '../models/IFilterClause';
import { FilterMap } from '../state/ContentExplorerState';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../state/SiteSpaceContentTypeState';
import {
  isBackgroundJobsSpace,
  isCatalogSpace,
  isFavoritesSpace,
  isPersonalSpace,
  isRecentsSpace,
  isRecommendationsSpace, isRecycleBinSpace, isResourceGovernanceSpace, isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteBroadcastsSpace,
  isSiteDatasourceSubspace,
  isSiteSpace,
  isSiteUserContentSubspace,
  isSiteUserRecycleBinSubspace,
  isSomeKindaSearchSpace,
  IsWorkbookDatasourceSubspaceTab,
  SpaceInfo
} from '../state/SpaceInfo';


// ---------- for project space

export const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'database',
  'table',
];

export const defaultContentTypeFilterValuesWithFlows: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'flow',
];

// ----- for favorites space (includes Views, which project space does not)

export const defaultContentTypeFilterValuesForFavoritesSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'view',
];

export function getDefaultContentTypeFilterValuesForRecycleBinSpace(): ReadonlyArray<ExploreContentApi_ContentType> {
  return FeatureFlags.recycleBinFlowsExpEnabled
    ? ['workbook', 'project', 'datasource', 'flow']
    : ['workbook', 'project', 'datasource'];
}

// For now, this is the same as for Favorites. But Collections DO NOT include collections, so if Favorites defaults add collections, this will have to diverge.
export const defaultContentTypeFilterValuesForCollectionSpace: ReadonlyArray<ExploreContentApi_ContentType> = defaultContentTypeFilterValuesForFavoritesSpace;

export const defaultContentTypeFilterValuesForSiteUserContentSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
];

export const defaultContentTypeFilterValuesForDatasourcesSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'datasource'
];

export const defaultContentTypeFilterValuesForRecentsSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'view',
  'flow'
];

// Content type filter is unused for the search space
export const defaultEmptyContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [];

export const defaultContentTypeFilterValuesForJobsSpace: ReadonlyArray<ExplorableItemType> = [
  'backgroundJob'
];

export const defaultContentTypeFilterValuesForResourceGovernanceSubspace: ReadonlyArray<ExplorableItemType> = [
  'resourceGovernanceSetting',
];

export const defaultContentTypeFilterValuesForRecommendationsSpace: ReadonlyArray<ExplorableItemType> = [
  'recommendedView'
];

export const defaultContentTypeFilterValuesForSharedWithMeSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'project',
  'workbook',
  'datasource',
  'view',
];

export const defaultContentTypeFilterValuesForPersonalSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  'workbook'
];

export const defaultContentTypeFilterValuesForBroadcastsSpace: ReadonlyArray<ExplorableItemType> = [
  'view'
];

export function selectFavoriteContentTypes(state: AppState): ReadonlyArray<ExploreContentApi_ContentType> {
  return selectFavoriteContentTypesSelector(state);
}

const selectFavoriteContentTypesSelector = createSelector(
  selectFlowsOnSiteEnabled,
  (isFlowsEnabled) => getFavoriteContentTypes(isFlowsEnabled)
);

export function selectSharedWithMeContentTypes(state: AppState): ReadonlyArray<ExploreContentApi_ContentType> {
  return selectSharedWithMeContentTypesSelector(state);
}

const selectSharedWithMeContentTypesSelector = createSelector(
  selectFlowsOnSiteEnabled,
  (isFlowsEnabled) => getSharedWithMeContentTypes(isFlowsEnabled)
);

export function getSharedWithMeContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes: Array<ExploreContentApi_ContentType> = Array.from(defaultContentTypeFilterValuesForSharedWithMeSpace);

  // TODO: While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForSharedWithMeSpace
  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes, isFlowsEnabled);

  if (CollectionUtils.checkCollectionSiteSetting()) {
    contentTypes.push('collection');
  }
  return contentTypes;
}

export function getFavoriteContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes = Array.from(defaultContentTypeFilterValuesForFavoritesSpace);

  // TODO: While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForFavoritesSpace
  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes as Array<ExploreContentApi_ContentType>, isFlowsEnabled);

  if (CollectionUtils.checkCollectionSiteSetting()) {
    contentTypes.push('collection');
  }
  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    contentTypes.push('virtualConnectionTable');
  }
  return contentTypes;
}

export function getRecycleBinContentTypes(): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes = Array.from(getDefaultContentTypeFilterValuesForRecycleBinSpace());
  return contentTypes;
}

export function getRecentContentTypes(): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes = Array.from(defaultContentTypeFilterValuesForRecentsSpace);

  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes as Array<ExploreContentApi_ContentType>);

  return contentTypes;
}

export function getCollectionItemsContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes = Array.from(defaultContentTypeFilterValuesForCollectionSpace);
  contentTypes = addConditionalContentTypesToBaseFilters(contentTypes as Array<ExploreContentApi_ContentType>, isFlowsEnabled);

  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    contentTypes.push('virtualConnectionTable');
  }

  return contentTypes;
}

function getSiteUserContentTypes(isFlowsEnabled: boolean): ReadonlyArray<ExploreContentApi_ContentType> {
  let contentTypes: Array<ExploreContentApi_ContentType> = Array.from(defaultContentTypeFilterValuesForSiteUserContentSubspace);

  // While removing a feature flag ('contentType') from addConditionalContentTypesToBaseFilters
  // add 'contentType' to defaultContentTypeFilterValuesForSiteUserContentSubspace

  return addConditionalContentTypesToBaseFilters(contentTypes, isFlowsEnabled);
}

/**
 * A helper method to add `flow`, `publishedConnection` content type
 * to filter, depending on whether they are available.
 * TODO: While removing the FeatureFlag, make sure you add to defaultContentTypeFilterValuesForXXXSpace
 * @param baseContentTypes Content filters which are always available for a space
 * @param isFlowsEnabled
 */
function addConditionalContentTypesToBaseFilters(
  baseContentTypes: Array<ExploreContentApi_ContentType>,
  isFlowsEnabled?: boolean): Array<ExploreContentApi_ContentType> {

  if (isFlowsEnabled) {
    baseContentTypes.push('flow');
  }

  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    baseContentTypes.push('virtualConnection');
  }

  return baseContentTypes;
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectDefaultContentTypeFilterValuesSelector = createSelector(
  [selectSpaceInfo, selectFlowsOnSiteEnabled],
  (spaceInfo, isFlowsEnabled) => getDefaultContentTypeFilterValues(spaceInfo, isFlowsEnabled)
);

export function getDefaultContentTypeFilterValues(spaceInfo: SpaceInfo, isFlowsEnabled: boolean): ReadonlyArray<ExplorableItemType> {

const server: SessionManagementService = getSessionManagementServiceInstance();

  if (IsWorkbookDatasourceSubspaceTab(spaceInfo) || isSiteDatasourceSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDatasourcesSubspace;
  }

  if (isRecycleBinSpace(spaceInfo)) {
    return getRecycleBinContentTypes();
  }

  if (isFavoritesSpace(spaceInfo)) {
    return getFavoriteContentTypes(isFlowsEnabled);
  }

  if (isSingleCollectionSpace(spaceInfo)) {
    return getCollectionItemsContentTypes(isFlowsEnabled);
  }

  if (isPersonalSpace(spaceInfo)) {
    let contentTypes: Array<ExploreContentApi_ContentType> = [];
    contentTypes = Array.from(defaultContentTypeFilterValuesForPersonalSpace);

    return contentTypes;
  }

  if (isSiteBroadcastsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForBroadcastsSpace;
  }

  if (isSiteUserContentSubspace(spaceInfo)) {
    return getSiteUserContentTypes(isFlowsEnabled);
  }

  if (isRecentsSpace(spaceInfo)) {
    return getRecentContentTypes();
  }

  if (isSomeKindaSearchSpace(spaceInfo)) {
    return defaultEmptyContentTypeFilterValues;
  }

  if (isBackgroundJobsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForJobsSpace;
  }

  if (isResourceGovernanceSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForResourceGovernanceSubspace;
  }

  if (isRecommendationsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForRecommendationsSpace;
  }

  if (isSharedWithMeSpace(spaceInfo)) {
    return getSharedWithMeContentTypes(isFlowsEnabled);
  }

  let contentTypes: Array<ExploreContentApi_ContentType> = [];
  if (isFlowsEnabled) {
    contentTypes = Array.from(defaultContentTypeFilterValuesWithFlows);
  } else {
    contentTypes = Array.from(defaultContentTypeFilterValues);
  }

  if (FeatureFlags.publishedConnectionContentManagementEnabled) {
    contentTypes.push('virtualConnection');
  }

  if (FeatureFlags.databasesInProjectsEnabled
    && FeatureFlags.databasesInProjectsPostMVPEnabled
    && server.catalogingOnSiteEnabled()) {
    contentTypes.push('database');
  }

  if (getSessionManagementServiceInstance().tablesInProjectsUIEnabled()) {
    contentTypes.push('table');
  }

  return contentTypes;
}

export function selectDefaultContentTypeFilterValues(state: AppState) {
  return selectDefaultContentTypeFilterValuesSelector(state);
}

export function selectProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues;
}

export function selectEffectiveProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues || selectDefaultContentTypeFilterValuesSelector(state);
}

export function selectMixedContentFilterClauses(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

export function selectSearchText(state: AppState): string {
  return (state.contentExplorer.mixedContentFilterClauses && state.contentExplorer.mixedContentFilterClauses.search &&
    (state.contentExplorer.mixedContentFilterClauses.search as IStringValuedFilterClause).value)
    || '';
}

export function selectSpecificMixedContentFilterClause(state: AppState, filterType: ContentFilterKeyName): IFilterClause {
  return state.contentExplorer.mixedContentFilterClauses[filterType];
}

export function selectSiteSpaceContentType(state: AppState): SiteSpaceContentType {
  return state.spaceInfo.subspaceName as SiteSpaceContentType;
}

export function selectMixedContentFilters(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectSiteSpaceFilterValues = createSelector(
  selectSiteSpaceContentType,
  (contentType) => [convertSiteContentTypeToContentType(contentType)]
);

export function selectCurrentlyApplicableContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  // This could be cleaned up by adding the current space (site or project) into the spaceInfo
  const spaceInfo = selectSpaceInfo(state);
  if (isSiteSpace(spaceInfo) || isCatalogSpace(spaceInfo)) {
    return selectSiteSpaceFilterValues(state);
  } else if (isSiteUserRecycleBinSubspace(spaceInfo)) {
    return state.contentExplorer.contentTypeFilterValues || getRecycleBinContentTypes();
  } else {
    return selectEffectiveProjectSpaceContentTypes(state);
  }
}

export function selectHiddenContentTypeForCurrentSpace(state: AppState) {
  return selectHiddenContentTypeForCurrentSpaceSelector(state);
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectHiddenContentTypeForCurrentSpaceSelector = createSelector(
  [selectSpaceInfo],
  (spaceInfo) => getHiddenContentType(spaceInfo)
);

function getHiddenContentType(spaceInfo: SpaceInfo): ReadonlyArray<ExplorableItemType> {
  // In case you want to hide a particular content type from content type filter dropdown for a space
  // add it here
  if (isSharedWithMeSpace(spaceInfo)) {
    const hiddenTypesForSharedWithMe: ExplorableItemType[] = [];
    if(!CollectionUtils.checkCollectionSiteSetting()){
      hiddenTypesForSharedWithMe.push('collection');
    }
    return hiddenTypesForSharedWithMe;
  }

  // if collections is not enabled in site settings hide it.
  if (!CollectionUtils.checkCollectionSiteSetting()){
    return ['collection'];
  }
  return [];
}
